<template>
  <div>
    <el-table
      v-if="!tableLoading"
      ref="orgSummaryTable"
      class="org-summary-table"
      :class="computedTableClass"
      :data="orgSummary"
      empty-text="No Data Available"
      header-cell-class-name="table-header-dark"
      row-class-name="table-row"
      :cell-class-name="tableCellClassName"
      @row-click="expandRow"
      @expand-change="handleExpandChange">
      <el-table-column
        prop="orgId"
        label="Org ID"
        min-width="80"
        show-overflow-tooltip
        label-class-name="column-header-double">
      </el-table-column>
      <el-table-column
        prop="orgName"
        label="Organization"
        min-width="220"
        show-overflow-tooltip
        label-class-name="column-header-double">
      </el-table-column>
      <el-table-column
        label="Allocated Date"
        min-width="80"
        show-overflow-tooltip
        label-class-name="column-header-double">
        <template slot-scope="scope">
          {{scope.row.allocationDate | moment('MM/DD/YYYY')}}
        </template>
      </el-table-column>
      <el-table-column
        label="Total"
        show-overflow-tooltip
        label-class-name="column-header-double">
        <template slot-scope="scope">
          {{scope.row.orgTotal | currency}}
        </template>
      </el-table-column>
      <el-table-column
        label="Characters of Allocation"
        header-align="center"
        label-class-name="table-header-light">
        <el-table-column label="A" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.orgTotalA | currency}}
          </template>
        </el-table-column>
        <el-table-column label="A1" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.orgTotalA1 | currency}}
          </template>
        </el-table-column>
        <el-table-column label="BC" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.orgTotalBC | currency}}
          </template>
        </el-table-column>
        <el-table-column label="F" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.orgTotalF | currency}}
          </template>
        </el-table-column>
        <el-table-column label="T" show-overflow-tooltip>
          <template slot-scope="scope">
            {{scope.row.orgTotalT | currency}}
          </template>
        </el-table-column>
        <el-table-column type="expand">
          <template slot-scope="props">
            <LoadingSpinner v-if="isRowLoading(props.row.index)"></LoadingSpinner>
            <el-table
              v-else
              :data="props.row.programAllocations"
              :span-method="arraySpanMethod"
              :show-header="false"
              empty-text="No Data Available"
              row-class-name="breakdown-table-row"
              class="breakdown-table"
              @row-click="goToAllocation">
              <el-table-column min-width="80"></el-table-column>
              <el-table-column min-width="220" show-overflow-tooltip>
                <template slot-scope="scope">
                  {{`${scope.row.programId} - ${scope.row.programName}`}}
                </template>
              </el-table-column>
              <el-table-column min-width="80" show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.approvedDate | moment("MM/DD/YYYY")}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationTotal | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationA | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationA1 | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationBC | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationF | currency}}
                </template>
              </el-table-column>
              <el-table-column show-overflow-tooltip>
                <template slot-scope="scope">
                  {{scope.row.allocationT | currency}}
                </template>
              </el-table-column>
              <el-table-column type="expand"></el-table-column>
            </el-table>
          </template>
        </el-table-column>
      </el-table-column>
    </el-table>
    <div v-if="!tableLoading" class="load-more-button-wrapper">
      <el-button
        v-if="showLoadMore"
        type="primary"
        @click="getNextPage"
        :loading="newResultsLoading"
        round
        plain>
        Load More
      </el-button>
    </div>
    <div class="loading-spinner-wrapper">
      <LoadingSpinner v-if="tableLoading" size="60px"></LoadingSpinner>
    </div>
  </div>
</template>

<script>
import LoadingSpinner from '../misc/LoadingSpinner.vue';
import api from '../../utils/api';
import * as Paths from '../../constants/paths';
import Constants from '../../constants';

export default {
  name: 'OrganzationSummaryTable',
  components: {
    LoadingSpinner,
  },
  computed: {
    computedTableClass() {
      if (this.isTableHeaderSticky && this.isMenuCollapsed) {
        return 'sticky-header-high';
      }
      if (this.isTableHeaderSticky) {
        return 'sticky-header-low';
      }
      return '';
    },
  },
  data() {
    return {
      orgSummary: [],
      tableLoading: false,
      loadingIndex: null,
      expandedCount: 0,
      tableColumns: ['orgId', 'orgName', 'allocationDate', 'orgTotal', 'orgTotalA', 'orgTotalA1', 'orgTotalBC', 'orgTotalF', 'orgTotalT'],
      pageNumber: 1,
      resultsPerPage: 20,
      newResultsLoading: false,
      showLoadMore: false,
      isTableHeaderSticky: false,
      isMenuCollapsed: false,
      lastScrollPosition: 0,
      scrollValue: 0,
      tableScrollOffset: Constants.SCROLL_OFFSETS.DASHBOARD_TABLE,
      menuScrollOffset: Constants.SCROLL_OFFSETS.MENU,
    };
  },
  methods: {
    tableCellClassName({
    // eslint-disable-next-line no-unused-vars
      row, column, rowIndex, columnIndex,
    }) {
      const className = ['table-cell'];
      if (this.orgSummary[rowIndex][this.tableColumns[columnIndex]] !== 0) {
        className.push('bold-cell');
      }
      return className.join(' ');
    },
    arraySpanMethod({
    // eslint-disable-next-line no-unused-vars
      row, column, rowIndex, columnIndex,
    }) {
      if (columnIndex === 0) {
        return [0, 0];
      }
      if (columnIndex === 1) {
        return [1, 2];
      }
      return [1, 1];
    },
    getNextPage() {
      this.pageNumber += 1;
      this.$emit('get-page', this.pageNumber);
    },
    getAndSetOrgSummary(bfy, orgId, pageNumber) {
      if (pageNumber === 1) {
        this.pageNumber = 1;
        this.tableLoading = true;
      } else {
        this.newResultsLoading = true;
      }
      api.get(Paths.ORG_SUMMARY_URL(bfy, orgId, pageNumber, this.resultsPerPage))
        .then((response) => {
          this.showLoadMore = this.resultsPerPage === response.data.data.length;
          // eslint-disable-next-line max-len
          this.orgSummary = pageNumber === 1 ? response.data.data : [...this.orgSummary, ...response.data.data];
        }).finally(() => {
          this.tableLoading = false;
          this.newResultsLoading = false;
          this.expandedCount = 0;
        });
    },
    expandRow(row) {
      this.$refs.orgSummaryTable.toggleRowExpansion(row);
    },
    handleExpandChange(row, expanded) {
      if (this.expandedCount < expanded.length) {
        this.getOrgSummaryPrograms(row.index, row.bfy, row.orgId);
      }
      this.expandedCount = expanded.length;
    },
    getOrgSummaryPrograms(rowIndex, bfy, orgId) {
      this.loadingIndex = rowIndex;
      api.get(Paths.ORG_SUMMARY_PROGRAM_URL(bfy, orgId)).then((response) => {
        // eslint-disable-next-line no-param-reassign,max-len

        // this.orgSummary[rowIndex].programAllocations = response.data.data;
        this.orgSummary[rowIndex].programAllocations = this.sortPrograms(response.data.data);
      }).finally(() => {
        this.loadingIndex = null;
      });
    },
    goToAllocation(row) {
      // this.$router.push(`/allocation/${row.programAllocationId}`);
      window.open(`${process.env.VUE_APP_BASE_URL}/#/allocation/${row.programAllocationId}`, '_blank');
    },
    isRowLoading(index) {
      return this.loadingIndex === index;
    },
    onScroll() {
      this.isTableHeaderSticky = window.pageYOffset >= this.tableScrollOffset;
      if (!(window.pageYOffset < 0)
        && !(Math.abs(window.pageYOffset - this.lastScrollPosition) < this.menuScrollOffset)) {
        this.isMenuCollapsed = window.pageYOffset >= this.lastScrollPosition;
        this.lastScrollPosition = window.pageYOffset;
      }
    },

    sortPrograms(array) {
      function sortByPrgmApproval(a, b) {
        if (a.programId < b.programId) return -1;
        if (a.programId > b.programId) return 1;
        if (a.approvedDate < b.approvedDate) return -1;
        if (a.approvedDate > b.approvedDate) return 1;
        return 0;
      }

      return array.sort(sortByPrgmApproval);
    },
  },
  mounted() {
    this.lastScrollPosition = window.pageYOffset;
    window.addEventListener('scroll', this.onScroll);
    const viewportMeta = document.createElement('meta');
    viewportMeta.name = 'viewport';
    viewportMeta.content = 'width=device-width, initial-scale=1';
    document.head.appendChild(viewportMeta);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.onScroll);
  },
};
</script>

<style scoped lang="scss">
  .org-summary-table {
    /deep/ .table-row {
      cursor: pointer;
    }
    /deep/ .el-table__expanded-cell {
      text-align: center;
    }
    /deep/ .breakdown-table-row {
      cursor: pointer;
    }
    &.sticky-header-high {
      position: relative;
      /deep/ .el-table__header-wrapper {
        position: fixed;
        top: 26px;
        z-index: 1;
        width: calc(100% - 112px);
      }
    }
    &.sticky-header-low {
      position: relative;
      /deep/ .el-table__header-wrapper {
        position: fixed;
        top: 51px;
        z-index: 1;
        width: calc(100% - 112px);
      }
    }
  }
  .loading-spinner-wrapper {
    text-align: center;
  }
</style>
